/**
 * The M6-A4 slot pickers: boards, cross-board field mappings, Slack destinations, the Gmail
 * message and the webhook destination (`specs/M6-A4-integrations.md` §7).
 *
 * Two of these are **deliberate differences from monday**, both for the same reason — monday
 * populates them by calling the third party while you type, and this product will not make a
 * call to Slack or Google to draw a picker:
 *
 * - the Slack channel/user pickers take an id (and an optional display name), and list the
 *   destinations this board's other automations already use, instead of `conversations.list`;
 * - the webhook slot asks for the URL outright, because monday hides it inside its `webhooks`
 *   app — which is exactly why the URL of all 42 imported rows was unrecoverable (§6).
 *
 * Every one of them shows a **"not connected"** state rather than a disabled control, so the
 * sentence still reads and the person can see what is missing (capture
 * `admin/connections--automation-connections.png`).
 */
import { useState } from 'react';
import type { IntegrationApp } from '@elizmonday/shared';
import { PopoverCaption, PopoverOption } from './SlotPopover';
import type { SlotEditorProps } from './pickers';

/** A one-line banner the integration slots share. */
export function NotConnectedNotice({ app }: { app: IntegrationApp }) {
  const name = APP_NAMES[app];
  return (
    <div className="ez-automation-popover__empty" data-testid={`slot-not-connected-${app}`}>
      {name} is not connected. An admin can connect it on Admin ▸ Connections.
    </div>
  );
}

export const APP_NAMES: Readonly<Record<IntegrationApp, string>> = {
  slack: 'Slack',
  gmail: 'Gmail',
  googleCalendar: 'Google Calendar',
  webhooks: 'Outgoing webhooks',
};

const isConnected = (props: SlotEditorProps, app: IntegrationApp): boolean =>
  props.pickers.connections?.[app] === 'connected';

/* ── boards ───────────────────────────────────────────────────────────────── */

/** `targetBoardId` / `linkageBoards`: the boards this person can write to. */
export function BoardPicker({ value, onChange, pickers }: SlotEditorProps) {
  const selected = value === null || value === undefined ? null : String(value);
  return (
    <>
      <PopoverCaption>Select a board</PopoverCaption>
      {pickers.boards.map((board) => (
        <PopoverOption
          key={board.id}
          selected={selected === board.id}
          testId={`slot-board-${board.id}`}
          onClick={() => onChange(Number(board.id))}
        >
          {board.name}
        </PopoverOption>
      ))}
      {pickers.boards.length === 0 ? <div className="ez-automation-popover__empty">No other board is available</div> : null}
    </>
  );
}

/* ── the cross-board field mapping ────────────────────────────────────────── */

interface MappingEntry {
  targetField?: { columnId?: string; title?: string };
  mappingParts?: Array<{ type: string; text?: string; sourceField?: { columnId?: string; title?: string } }>;
}

/** One line per target column: "Name ← <source column or text>". */
export function summariseMapping(value: unknown): Array<{ target: string; source: string }> {
  const mapping = (value ?? {}) as { mappingConfig?: Record<string, MappingEntry> };
  return Object.entries(mapping.mappingConfig ?? {}).map(([key, entry]) => ({
    target: entry.targetField?.title ?? entry.targetField?.columnId ?? key,
    source:
      (entry.mappingParts ?? [])
        .map((part) => (part.type === 'field' ? (part.sourceField?.title ?? part.sourceField?.columnId ?? '?') : (part.text ?? '')))
        .filter((piece) => piece.length > 0)
        .join(' + ') || '(empty)',
  }));
}

/**
 * The mapping editor. Read-only for an **imported** mapping, because monday's config names
 * columns on boards this product may not have imported yet and silently rewriting it would
 * lose the original; a new mapping is built one row at a time from this board's columns.
 */
export function ItemMappingEditor({ value, onChange, pickers, close }: SlotEditorProps) {
  const rows = summariseMapping(value);
  const [target, setTarget] = useState('');
  const [source, setSource] = useState('');

  const add = (): void => {
    if (target.length === 0) return;
    const mapping = (value ?? { groupId: null, mappingConfig: {} }) as {
      groupId: string | null;
      mappingConfig: Record<string, MappingEntry>;
    };
    const sourceColumn = pickers.columns.find((column) => column.id === source);
    onChange({
      ...mapping,
      mappingConfig: {
        ...mapping.mappingConfig,
        [target]: {
          targetField: { columnId: target, title: target },
          mappingParts:
            sourceColumn === undefined
              ? [{ type: 'text', text: source }]
              : [{ type: 'field', sourceField: { columnId: sourceColumn.id, title: sourceColumn.title } }],
        },
      },
    });
    setTarget('');
    setSource('');
  };

  return (
    <div className="ez-automation-form" data-testid="slot-item-mapping">
      <PopoverCaption>Fields on the new item</PopoverCaption>
      {rows.length === 0 ? <div className="ez-automation-popover__empty">Nothing is mapped yet</div> : null}
      {rows.map((row) => (
        <div key={row.target} className="ez-automation-mapping-row" data-testid={`mapping-row-${row.target}`}>
          <span className="ez-automation-mapping-row__target">{row.target}</span>
          <span aria-hidden="true">←</span>
          <span className="ez-automation-mapping-row__source">{row.source}</span>
        </div>
      ))}
      <input
        className="ez-automation-input"
        placeholder="Target column id"
        aria-label="Target column id"
        data-testid="mapping-target"
        value={target}
        onChange={(event) => setTarget(event.target.value)}
      />
      <select
        className="ez-automation-input"
        aria-label="Source"
        data-testid="mapping-source"
        value={source}
        onChange={(event) => setSource(event.target.value)}
      >
        <option value="">Fixed text or a source column…</option>
        {pickers.columns.map((column) => (
          <option key={column.id} value={column.id}>
            {column.title}
          </option>
        ))}
      </select>
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" data-testid="mapping-close" onClick={close}>
          Close
        </button>
        <button type="button" className="ez-automation-button" data-testid="mapping-add" onClick={add}>
          Add field
        </button>
      </div>
    </div>
  );
}

/* ── Slack ────────────────────────────────────────────────────────────────── */

function SlackTargetEditor(props: SlotEditorProps & { kind: 'channel' | 'user' }) {
  const current = (props.value ?? null) as { value?: string; title?: string } | null;
  const [id, setId] = useState(current?.value ?? '');
  const [title, setTitle] = useState(current?.title ?? '');
  const connected = isConnected(props, 'slack');
  return (
    <div className="ez-automation-form" data-testid={`slot-slack-${props.kind}`}>
      {connected ? null : <NotConnectedNotice app="slack" />}
      <PopoverCaption>{props.kind === 'channel' ? 'Slack channel' : 'Slack user'}</PopoverCaption>
      <input
        className="ez-automation-input"
        placeholder={props.kind === 'channel' ? 'C0123456789' : 'U0123456789'}
        aria-label={props.kind === 'channel' ? 'Slack channel id' : 'Slack user id'}
        data-testid="slack-id"
        value={id}
        onChange={(event) => setId(event.target.value.trim())}
      />
      <input
        className="ez-automation-input"
        placeholder={props.kind === 'channel' ? 'store-updates' : 'Carolina'}
        aria-label="Display name"
        data-testid="slack-title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" data-testid="slack-cancel" onClick={props.close}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-automation-button"
          data-testid="slack-done"
          disabled={id.length === 0}
          onClick={() => {
            props.onChange({ value: id, title: title.length === 0 ? id : title });
            props.close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

export const SlackChannelPicker = (props: SlotEditorProps) => <SlackTargetEditor {...props} kind="channel" />;
export const SlackUserPicker = (props: SlotEditorProps) => <SlackTargetEditor {...props} kind="user" />;

/** monday keeps `connection` read-only once chosen; with one connection per app, so do we. */
export function ConnectionSlot(props: SlotEditorProps) {
  const connection = (props.value ?? {}) as { authProvider?: string };
  const app = (connection.authProvider ?? 'slack').toLowerCase() as IntegrationApp;
  const name = APP_NAMES[app] ?? connection.authProvider ?? 'the app';
  return (
    <div className="ez-automation-form" data-testid="slot-connection">
      <PopoverCaption>Connection</PopoverCaption>
      <div className="ez-automation-popover__empty">
        This automation runs through the account&apos;s {name} connection, managed on Admin ▸ Connections.
      </div>
    </div>
  );
}

/* ── Gmail ────────────────────────────────────────────────────────────────── */

export function EmailEditor(props: SlotEditorProps) {
  const current = (props.value ?? {}) as { subject?: string; body?: string };
  const [subject, setSubject] = useState(current.subject ?? '');
  const [body, setBody] = useState(current.body ?? '');
  return (
    <div className="ez-automation-form" data-testid="slot-email">
      {isConnected(props, 'gmail') ? null : <NotConnectedNotice app="gmail" />}
      <PopoverCaption>Email</PopoverCaption>
      <input
        className="ez-automation-input"
        placeholder="Subject"
        aria-label="Subject"
        data-testid="email-subject"
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
      />
      <textarea
        className="ez-automation-input ez-automation-input--multiline"
        placeholder="Message — {pulse.name} and {board.name} work here too"
        aria-label="Body"
        data-testid="email-body"
        rows={4}
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" data-testid="email-cancel" onClick={props.close}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-automation-button"
          data-testid="email-done"
          onClick={() => {
            props.onChange({ subject, body });
            props.close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/* ── Google Calendar ──────────────────────────────────────────────────────── */

/** The same mapping editor, with Google's five field names as the targets. */
export const CALENDAR_FIELDS = ['summary', 'description', 'location', 'start', 'end'] as const;

export function EventMappingEditor(props: SlotEditorProps) {
  return (
    <>
      {isConnected(props, 'googleCalendar') ? null : <NotConnectedNotice app="googleCalendar" />}
      <ItemMappingEditor {...props} />
    </>
  );
}

/* ── the generic webhook ──────────────────────────────────────────────────── */

export function WebhookUrlEditor(props: SlotEditorProps) {
  const [url, setUrl] = useState(typeof props.value === 'string' ? props.value : '');
  const invalid = url.length > 0 && !/^https:\/\/|^http:\/\/localhost/.test(url);
  return (
    <div className="ez-automation-form" data-testid="slot-webhook-url">
      <PopoverCaption>Where to POST</PopoverCaption>
      <input
        className="ez-automation-input"
        placeholder="https://example.com/hooks/elizmonday"
        aria-label="Webhook URL"
        data-testid="webhook-url"
        value={url}
        onChange={(event) => setUrl(event.target.value.trim())}
      />
      {invalid ? (
        <div className="ez-automation-popover__empty" data-testid="webhook-url-invalid">
          Use https (http is only allowed to localhost).
        </div>
      ) : null}
      <div className="ez-automation-popover__empty">
        The host must be on the outgoing allow-list an admin sets on Admin ▸ Connections.
      </div>
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" data-testid="webhook-url-cancel" onClick={props.close}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-automation-button"
          data-testid="webhook-url-done"
          disabled={url.length === 0 || invalid}
          onClick={() => {
            props.onChange(url);
            props.close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/* ── the board template slot (`88520`) ────────────────────────────────────── */

export function BoardMappingEditor(props: SlotEditorProps) {
  const current = (props.value ?? {}) as { boardName?: string; templateId?: number | string | null };
  const [name, setName] = useState(current.boardName ?? '');
  const [templateId, setTemplateId] = useState(current.templateId === null || current.templateId === undefined ? '' : String(current.templateId));
  return (
    <div className="ez-automation-form" data-testid="slot-board-mapping">
      <PopoverCaption>New board</PopoverCaption>
      <input
        className="ez-automation-input"
        placeholder="PRJ-{pulse.name}"
        aria-label="Board name"
        data-testid="board-mapping-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <input
        className="ez-automation-input"
        placeholder="Template id"
        aria-label="Template id"
        data-testid="board-mapping-template"
        value={templateId}
        onChange={(event) => setTemplateId(event.target.value.trim())}
      />
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" data-testid="board-mapping-cancel" onClick={props.close}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-automation-button"
          data-testid="board-mapping-done"
          onClick={() => {
            props.onChange({ ...current, boardName: name, templateId: templateId.length === 0 ? null : templateId });
            props.close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
